import { useQuery } from '@tanstack/react-query';
import type { SearchGroup, SearchResponse, SearchSource } from '@asyagrup/group-hub-contracts';
import { api } from '@/lib/api/client';

export const searchKeys = {
  all: ['search'] as const,
  query: (q: string, sources: string | null) => ['search', 'query', q, sources ?? '*'] as const,
};

/**
 * One request per settled term.
 *
 * `signal` is threaded through on purpose: react-query aborts the superseded
 * request when the key changes, which is the entire cancellation mechanism
 * here. Without it a slow answer to "ah" can land after the answer to "ahmet"
 * and repaint the list with the wrong rows.
 */
export function useGlobalSearch(
  term: string,
  options: { enabled: boolean; sources?: readonly SearchSource[] | null },
) {
  const sources = options.sources?.length ? [...options.sources].join(',') : null;
  const params = new URLSearchParams({ q: term });
  if (sources) params.set('sources', sources);

  return useQuery({
    queryKey: searchKeys.query(term, sources),
    queryFn: ({ signal }) => api.get<SearchResponse>(`/search?${params.toString()}`, signal),
    enabled: options.enabled,
    // A search term is answered by rows that change on the scale of minutes,
    // not keystrokes; re-typing the same term should not re-ask.
    staleTime: 60_000,
    // Keeps the previous answer on screen while the next term loads, so the
    // panel dims rather than blanking once per keystroke.
    placeholderData: (previous) => previous,
    // 401/403/404 already do not retry (see main.tsx); a search that fails
    // twice more before saying so just makes the box feel broken for longer.
    retry: false,
  });
}

/**
 * Paletin sunucu grupları — «Diğer kaynaklarda da ara» YALNIZ sorulduysa.
 *
 * İkinci sorgu kaynak listesi yokken birincininkiyle AYNI anahtarı taşıyor
 * (`['search','query',terim,'*']`). Kapalı bir sorgu önbellekteki veriyi yine
 * döndürür; yani soru sorulmadan `ek` birincinin cevabının ta kendisiydi ve
 * palet her grubu İKİ KEZ çiziyordu — aynı satır kimlikleriyle, ki klavyeyle
 * gezinmeyi de şaşırtıyordu (DST-00132 doğrulanırken ekranda görüldü; #89'dan
 * beri böyle). Anahtarı ayırmak da çözerdi, ama asıl kural şu: sorulmamış bir
 * sorunun cevabı listede olamaz.
 */
export function sunucuGruplari(
  birincil: SearchResponse | undefined,
  ek: SearchResponse | undefined,
  ekSoruldu: boolean,
): SearchGroup[] {
  return [...(birincil?.groups ?? []), ...(ekSoruldu ? (ek?.groups ?? []) : [])];
}

/**
 * A pasted IBAN, reduced to its last four digits BEFORE the request exists.
 *
 * The full number never reaches the server, an access log, or a URL — the
 * whole account identifier is stored encrypted on the other side, and sending
 * it as a query parameter to look it up would undo that in one line. Four
 * digits are exactly what `fin_payee.iban_last4` holds and exactly what the
 * cari screen already shows, so nothing new is exposed.
 *
 * Returns null for anything that is not IBAN-shaped, including the bare
 * four-digit term someone may have typed on purpose.
 */
export function ibanLast4(raw: string): string | null {
  const compact = raw.replace(/\s+/g, '').toLocaleUpperCase('tr');
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(compact)) return null;
  const last4 = compact.slice(-4);
  return /^\d{4}$/.test(last4) ? last4 : null;
}
